import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import LandingPage from "@/pages/LandingPage";
import TryItNowPage from "@/pages/TryItNowPage";
import AuthPage from "@/pages/AuthPage";
import OnboardingPage from "@/pages/OnboardingPage";
import HomePage from "@/pages/HomePage";
import ConnectPage from "@/pages/ConnectPage";
import LearnPage from "@/pages/LearnPage";
import LearnPathDetailPage from "@/pages/LearnPathDetailPage";
import CommunityPage from "@/pages/CommunityPage";
import CircleDetailPage from "@/pages/CircleDetailPage";
import WatchPage from "@/pages/WatchPage";
import GivePage from "@/pages/GivePage";
import GuidedPrayerPage from "@/pages/GuidedPrayerPage";
import PersonalPrayerStartPage from "@/pages/PersonalPrayerStartPage";
import PersonalPrayerPage from "@/pages/PersonalPrayerPage";
import ProfilePage from "@/pages/ProfilePage";
import WorshipPage from "@/pages/WorshipPage";
import JournalPage from "@/pages/JournalPage";
import AdminPage from "@/pages/AdminPage";
import AppLayout from "@/components/AppLayout";

function ProtectedRoute({ children, adminOnly = false }) {
  const { user, loading } = useAuth();
  if (loading)
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-[#C4A265] font-heading text-2xl animate-pulse">
          VO✝CE
        </div>
      </div>
    );
  if (!user) return <Navigate to="/auth" />;
  if (adminOnly && user.role !== "admin") return <Navigate to="/home" />;
  return children;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/try" element={<TryItNowPage />} />
      <Route path="/auth" element={<AuthPage />} />
      <Route path="/onboarding" element={<ProtectedRoute><OnboardingPage /></ProtectedRoute>} />
      <Route path="/home" element={<ProtectedRoute><AppLayout><HomePage /></AppLayout></ProtectedRoute>} />
      <Route path="/watch" element={<ProtectedRoute><AppLayout><WatchPage /></AppLayout></ProtectedRoute>} />
      <Route path="/learn" element={<ProtectedRoute><AppLayout><LearnPage /></AppLayout></ProtectedRoute>} />
      <Route path="/learn/:slug" element={<ProtectedRoute><AppLayout><LearnPathDetailPage /></AppLayout></ProtectedRoute>} />
      <Route path="/community" element={<ProtectedRoute><AppLayout><CommunityPage /></AppLayout></ProtectedRoute>} />
      <Route path="/circles/:slug" element={<ProtectedRoute><AppLayout><CircleDetailPage /></AppLayout></ProtectedRoute>} />
      <Route path="/give" element={<ProtectedRoute><AppLayout><GivePage /></AppLayout></ProtectedRoute>} />
      <Route path="/connect" element={<ProtectedRoute><AppLayout headerless><ConnectPage /></AppLayout></ProtectedRoute>} />
      <Route path="/connect/:botId" element={<ProtectedRoute><AppLayout headerless><ConnectPage /></AppLayout></ProtectedRoute>} />
      <Route path="/guided-prayer" element={<ProtectedRoute><GuidedPrayerPage /></ProtectedRoute>} />
      <Route path="/guided-prayer/new" element={<ProtectedRoute><PersonalPrayerStartPage /></ProtectedRoute>} />
      <Route path="/guided-prayer/personal/:id" element={<ProtectedRoute><PersonalPrayerPage /></ProtectedRoute>} />
      <Route path="/guided-prayer/:journeyId" element={<ProtectedRoute><GuidedPrayerPage /></ProtectedRoute>} />
      <Route path="/worship" element={<ProtectedRoute><AppLayout><WorshipPage /></AppLayout></ProtectedRoute>} />
      <Route path="/profile" element={<ProtectedRoute><AppLayout><ProfilePage /></AppLayout></ProtectedRoute>} />
      <Route path="/journal" element={<ProtectedRoute><AppLayout><JournalPage /></AppLayout></ProtectedRoute>} />
      <Route path="/admin" element={<ProtectedRoute adminOnly><AppLayout><AdminPage /></AppLayout></ProtectedRoute>} />
      <Route path="/dashboard" element={<Navigate to="/home" />} />
      <Route path="/chat" element={<Navigate to="/connect" />} />
      <Route path="/chat/:botId" element={<Navigate to="/connect" />} />
      <Route path="*" element={<Navigate to="/" />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        <Toaster richColors position="top-right" />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
